// src/utils/auth.js
import { USER_ROLES, ROLE_HIERARCHY } from '../store/authSlice';

// Permission constants
export const PERMISSIONS = {
  // Dashboard permissions
  DASHBOARD_VIEW: 'dashboard_view',
  
  // User management permissions
  USERS_VIEW: 'users_view',
  USERS_CREATE: 'users_create',
  USERS_EDIT: 'users_edit',
  USERS_DELETE: 'users_delete',
  USERS_MANAGE: 'users_manage', // Includes all user operations
  
  // Car management permissions
  CARS_VIEW: 'cars_view',
  CARS_CREATE: 'cars_create',
  CARS_EDIT: 'cars_edit',
  CARS_DELETE: 'cars_delete',
  CARS_MANAGE: 'cars_manage', // Includes all car operations
  
  // Location management permissions
  LOCATIONS_VIEW: 'locations_view',
  LOCATIONS_MANAGE: 'locations_manage',
  
  // Trip management permissions
  TRIPS_VIEW: 'trips_view',
  TRIPS_MANAGE: 'trips_manage',
  
  // Driver management permissions
  DRIVERS_VIEW: 'drivers_view',
  DRIVERS_CREATE: 'drivers_create',
  DRIVERS_EDIT: 'drivers_edit',
  DRIVERS_DELETE: 'drivers_delete',
  DRIVERS_MANAGE: 'drivers_manage', // Includes all driver operations
  DRIVERS_VERIFY: 'drivers_verify', // Driver verification permissions

  // Notification permissions
  NOTIFICATIONS_VIEW: 'notifications_view',
  NOTIFICATIONS_MANAGE: 'notifications_manage',

  // System administration permissions
  SYSTEM_ADMIN: 'system_admin',
  USER_ROLES_MANAGE: 'user_roles_manage'
};

// Role-permission mapping
const ADMIN_PERMISSIONS = [
  PERMISSIONS.DASHBOARD_VIEW,
  PERMISSIONS.USERS_VIEW,
  PERMISSIONS.USERS_CREATE,
  PERMISSIONS.USERS_EDIT,
  PERMISSIONS.USERS_MANAGE,
  PERMISSIONS.CARS_VIEW,
  PERMISSIONS.CARS_CREATE,
  PERMISSIONS.CARS_EDIT,
  PERMISSIONS.CARS_DELETE,
  PERMISSIONS.CARS_MANAGE,
  PERMISSIONS.LOCATIONS_VIEW,
  PERMISSIONS.LOCATIONS_MANAGE,
  PERMISSIONS.TRIPS_VIEW,
  PERMISSIONS.TRIPS_MANAGE,
  PERMISSIONS.DRIVERS_VIEW,
  PERMISSIONS.DRIVERS_CREATE,
  PERMISSIONS.DRIVERS_EDIT,
  PERMISSIONS.DRIVERS_DELETE,
  PERMISSIONS.DRIVERS_MANAGE,
  PERMISSIONS.DRIVERS_VERIFY,
  PERMISSIONS.NOTIFICATIONS_VIEW,
  PERMISSIONS.NOTIFICATIONS_MANAGE
];

export const ROLE_PERMISSIONS = {
  [USER_ROLES.USER]: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.TRIPS_VIEW, // Own trips only
    PERMISSIONS.NOTIFICATIONS_VIEW // Own notifications only
  ],

  [USER_ROLES.DRIVER]: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.TRIPS_VIEW, // Driver-specific trip access
    PERMISSIONS.TRIPS_MANAGE, // Can accept/reject trips
    PERMISSIONS.NOTIFICATIONS_VIEW
  ],

  [USER_ROLES.ADMIN]: ADMIN_PERMISSIONS,

  [USER_ROLES.SUPER_ADMIN]: [
    ...ADMIN_PERMISSIONS,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.SYSTEM_ADMIN,
    PERMISSIONS.USER_ROLES_MANAGE
  ]
};

// Route-permission mapping
export const ROUTE_PERMISSIONS = {
  '/dashboard': [PERMISSIONS.DASHBOARD_VIEW],

  // User management routes
  '/users': [PERMISSIONS.USERS_VIEW, PERMISSIONS.USERS_MANAGE],
  '/users/add': [PERMISSIONS.USERS_CREATE, PERMISSIONS.USERS_MANAGE],
  '/users/edit': [PERMISSIONS.USERS_EDIT, PERMISSIONS.USERS_MANAGE],

  // Car management routes
  '/cars': [PERMISSIONS.CARS_VIEW, PERMISSIONS.CARS_MANAGE],
  '/cars/add': [PERMISSIONS.CARS_CREATE, PERMISSIONS.CARS_MANAGE],
  '/cars/edit': [PERMISSIONS.CARS_EDIT, PERMISSIONS.CARS_MANAGE],

  // Location management routes
  '/locations': [PERMISSIONS.LOCATIONS_VIEW, PERMISSIONS.LOCATIONS_MANAGE],

  // Trip management routes
  '/trips': [PERMISSIONS.TRIPS_VIEW, PERMISSIONS.TRIPS_MANAGE],

  // Driver management routes
  '/drivers': [PERMISSIONS.DRIVERS_VIEW, PERMISSIONS.DRIVERS_MANAGE],

  // Finance routes (admin only)
  '/finance': [PERMISSIONS.CARS_MANAGE, PERMISSIONS.TRIPS_MANAGE], // Finance requires car and trip management

  // Notification routes
  '/notifications': [PERMISSIONS.NOTIFICATIONS_VIEW]
};

// Permission checking utilities
export const hasRole = (user, role) => {
  if (!user || !user.role) return false;
  return user.role === role;
};

export const hasMinimumRole = (user, minimumRole) => {
  if (!user || !user.role) return false;
  const userLevel = ROLE_HIERARCHY[user.role] || 0;
  const requiredLevel = ROLE_HIERARCHY[minimumRole] || 0;
  return userLevel >= requiredLevel;
};

export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  
  // Check if user has explicit permission
  if (user.permissions && user.permissions.includes(permission)) {
    return true;
  }
  
  // Check role-based permissions
  const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
  return rolePermissions.includes(permission);
};

export const hasAnyPermission = (user, permissions) => {
  if (!permissions || permissions.length === 0) return true;
  return permissions.some(permission => hasPermission(user, permission));
};

export const hasAllPermissions = (user, permissions) => {
  if (!permissions || permissions.length === 0) return true;
  return permissions.every(permission => hasPermission(user, permission));
};

export const canAccessRoute = (user, route) => {
  const routePermissions = ROUTE_PERMISSIONS[route];
  if (!routePermissions) return true; // Public route
  return hasAnyPermission(user, routePermissions);
};

// Navigation filtering utilities
export const getAccessibleNavItems = (user, navItems) => {
  if (!user) return [];
  
  return navItems.filter(item => {
    if (!item.to) return true; // Items without routes are always visible
    return canAccessRoute(user, item.to);
  });
};

// User role utilities
export const isAdmin = (user) => hasMinimumRole(user, USER_ROLES.ADMIN);
export const isSuperAdmin = (user) => hasRole(user, USER_ROLES.SUPER_ADMIN);
export const isUser = (user) => hasRole(user, USER_ROLES.USER);
export const isDriver = (user) => hasRole(user, USER_ROLES.DRIVER);

// Authentication validation utilities
export const isAuthenticated = (authState) => {
  return Boolean(
    authState &&
    authState.isAuthenticated &&
    authState.user &&
    authState.token
  );
};

export const getUserDisplayName = (user) => {
  if (!user) return 'Guest';
  return user.name || user.email || 'User';
};

export const getUserInitials = (user) => {
  if (!user || !user.name) return 'U';
  
  const names = user.name.split(' ');
  if (names.length >= 2) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase();
  }
  return names[0][0].toUpperCase();
};

// Role display utilities
export const getRoleDisplayName = (role) => {
  const roleNames = {
    [USER_ROLES.USER]: 'User',
    [USER_ROLES.DRIVER]: 'Driver',
    [USER_ROLES.ADMIN]: 'Administrator',
    [USER_ROLES.SUPER_ADMIN]: 'Super Administrator'
  };

  return roleNames[role] || 'Unknown';
};

export const getRoleBadgeColor = (role) => {
  const colors = {
    [USER_ROLES.USER]: 'bg-blue-100 text-blue-800',
    [USER_ROLES.DRIVER]: 'bg-yellow-100 text-yellow-800',
    [USER_ROLES.ADMIN]: 'bg-green-100 text-green-800',
    [USER_ROLES.SUPER_ADMIN]: 'bg-purple-100 text-purple-800'
  };

  return colors[role] || 'bg-gray-100 text-gray-800';
};

// Session management utilities
export const shouldRefreshToken = (token, thresholdMinutes = 15) => {
  if (!token) return false;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = payload.exp - currentTime;
    const thresholdSeconds = thresholdMinutes * 60;
    
    return timeUntilExpiry <= thresholdSeconds && timeUntilExpiry > 0;
  } catch {
    return false;
  }
};

export const getTokenExpiryTime = (token) => {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    return new Date(payload.exp * 1000);
  } catch {
    return null;
  }
};

// Error handling utilities
export const getAuthErrorMessage = (error) => {
  const errorMessages = {
    'Invalid credentials': 'Invalid email or password. Please try again.',
    'Network Error': 'Unable to connect to the server. Please check your internet connection.',
    'Unauthorized': 'Your session has expired. Please log in again.',
    'Forbidden': 'You do not have permission to access this resource.',
    'Too Many Requests': 'Too many login attempts. Please try again later.'
  };
  
  return errorMessages[error] || 'An unexpected error occurred. Please try again.';
};
